import { useEffect, useRef, useState, useCallback } from 'react';

export interface NotificationMessage {
  id: string;
  type: 'info' | 'success' | 'warning' | 'error';
  title: string;
  message: string;
  timestamp: string;
  data?: any;
  read: boolean;
  category: 'system' | 'rental' | 'contract' | 'payment' | 'maintenance';
  reference_type?: string;
  reference_id?: number;
}

export interface WebSocketMessage {
  type: 'notification' | 'data_update' | 'user_activity' | 'system_status' | 'ping' | 'pong';
  payload: any;
}

export interface UseWebSocketOptions {
  url?: string;
  autoConnect?: boolean;
  baseReconnectInterval?: number;  // Base delay for exponential backoff
  maxReconnectInterval?: number;   // Max delay cap for backoff
  maxReconnectAttempts?: number;
  onMessage?: (message: any) => void;
  onConnected?: () => void;
  onDisconnected?: () => void;
  onError?: (error: Event) => void;
}

export interface UseWebSocketReturn {
  isConnected: boolean;
  isConnecting: boolean;
  connectionError: string | null;
  connect: () => void;
  disconnect: () => void;
  sendMessage: (message: WebSocketMessage) => void;
}

// Calculate exponential backoff delay with jitter
const calculateBackoffDelay = (
  attempt: number,
  baseDelay: number,
  maxDelay: number
): number => {
  // Exponential backoff: baseDelay * 2^attempt
  const exponentialDelay = baseDelay * Math.pow(2, attempt);
  // Cap at maxDelay
  const cappedDelay = Math.min(exponentialDelay, maxDelay);
  // Add jitter (±10%) to prevent thundering herd
  const jitter = cappedDelay * 0.1 * (Math.random() * 2 - 1);
  return Math.floor(cappedDelay + jitter);
};

/**
 * ลายนิ้วมือของ access token ใช้ตรวจว่า token เปลี่ยนหรือไม่ (login/logout/refresh)
 * KD3 S2562 (FLOW-NOTIF-031): เดิมใช้ 20 ตัวแรกของ JWT ซึ่งเป็น header คงที่ทุก token
 * (`eyJhbGciOiJIUzI1NiIs…`) → refresh token แล้ว fingerprint ไม่เปลี่ยน ไม่เคยส่ง token_refresh
 * ให้ server · ใช้ส่วนลายเซ็น (segment สุดท้าย) ซึ่งต่างกันทุก token แทน
 */
export const accessTokenFingerprint = (token: string | null): string | null => {
  if (!token) return null;
  const parts = token.split('.');
  return parts.length === 3 ? parts[2] : token;
};

const useWebSocket = (options: UseWebSocketOptions = {}): UseWebSocketReturn => {
  const {
    url = `${import.meta.env.VITE_API_URL?.replace(/^http/, 'ws') || 'ws://localhost:20003'}/api/v1/ws`,
    autoConnect = true,
    baseReconnectInterval = 1000,   // Start at 1 second
    maxReconnectInterval = 30000,   // Cap at 30 seconds
    maxReconnectAttempts = 7,       // With exponential backoff: 1s, 2s, 4s, 8s, 16s, 30s, 30s
    onConnected,
    onDisconnected,
    onError
  } = options;

  // Keep onMessage in a ref so it's always fresh without causing reconnect loops
  const onMessageRef = useRef<UseWebSocketOptions['onMessage']>(options.onMessage);
  useEffect(() => {
    onMessageRef.current = options.onMessage;
  });

  const ws = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const tokenChangeReconnectRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const reconnectAttemptsRef = useRef(0);

  // Use refs to track connection state to avoid useEffect dependency cycles
  const isConnectingRef = useRef(false);
  const isConnectedRef = useRef(false);
  const manuallyDisconnectedRef = useRef(false);

  const [isConnected, setIsConnected] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [connectionError, setConnectionError] = useState<string | null>(null);

  // Track token value for reconnection after login/logout
  const getTokenFingerprint = () => accessTokenFingerprint(localStorage.getItem('access_token'));
  const [tokenFingerprint, setTokenFingerprint] = useState<string | null>(getTokenFingerprint);
  const prevTokenRef = useRef<string | null>(tokenFingerprint);

  // Track previous autoConnect value to detect changes from false -> true
  const prevAutoConnectRef = useRef<boolean>(autoConnect);

  // Sync refs with state
  useEffect(() => {
    isConnectedRef.current = isConnected;
  }, [isConnected]);

  useEffect(() => {
    isConnectingRef.current = isConnecting;
  }, [isConnecting]);

  // ฟังก์ชันสำหรับเชื่อมต่อ WebSocket
  const connect = useCallback(() => {
    if (ws.current?.readyState === WebSocket.OPEN) {
      console.log('WebSocket already connected');
      return;
    }

    if (isConnectingRef.current) {
      console.log('WebSocket connection already in progress');
      return;
    }

    try {
      manuallyDisconnectedRef.current = false;
      isConnectingRef.current = true;
      setIsConnecting(true);
      setConnectionError(null);

      const token = localStorage.getItem('access_token');
      if (!token) {
        console.warn('No auth token found, skipping WebSocket connection');
        isConnectingRef.current = false;
        setIsConnecting(false);
        return;
      }

      console.log('Connecting to WebSocket:', url);
      ws.current = new WebSocket(url);

      ws.current.onopen = () => {
        console.log('WebSocket connection opened, waiting for auth_required...');
      };

      ws.current.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);
          console.log('WebSocket message received:', message);

          // Use event.target to prevent race condition with multiple connections
          const wsInstance = event.target as WebSocket;

          // Handle auth_required — send token via auth message (more secure than URL)
          if (message.type === 'auth_required') {
            console.log('Received auth_required, sending auth token...');
            if (token && wsInstance.readyState === WebSocket.OPEN) {
              wsInstance.send(JSON.stringify({
                type: 'auth',
                token: token
              }));
              console.log('Auth token sent successfully');
            } else {
              console.error('Cannot send auth - token:', token ? 'present' : 'missing', 'readyState:', wsInstance.readyState);
              setConnectionError('ไม่พบ token สำหรับยืนยันตัวตน');
              isConnectingRef.current = false;
              setIsConnecting(false);
            }
            return; // Do NOT forward internal protocol messages to onMessage
          }

          // Handle connected — auth succeeded
          if (message.type === 'connected') {
            console.log('WebSocket authenticated successfully:', message);
            isConnectedRef.current = true;
            isConnectingRef.current = false;
            setIsConnected(true);
            setIsConnecting(false);
            setConnectionError(null);
            reconnectAttemptsRef.current = 0;
            onConnected?.();
            return; // Do NOT forward internal protocol messages to onMessage
          }

          // Handle pong — heartbeat response
          if (message.type === 'pong') {
            console.log('WebSocket heartbeat pong received');
            return; // Do NOT forward internal protocol messages to onMessage
          }

          // Handle error messages
          if (message.type === 'error') {
            console.error('WebSocket error message:', message.message);
            setConnectionError(message.message);
            return; // Do NOT forward internal protocol messages to onMessage
          }

          // Forward all application-level messages to the shared onMessage callback via ref
          // Using ref avoids stale closure and prevents reconnect loops
          onMessageRef.current?.(message);

        } catch (error) {
          console.error('Error parsing WebSocket message:', error);
        }
      };

      ws.current.onclose = (event) => {
        console.log('WebSocket disconnected:', event.code, event.reason);
        isConnectedRef.current = false;
        isConnectingRef.current = false;
        setIsConnected(false);
        setIsConnecting(false);

        if (event.code !== 1000 && event.code !== 1001) {
          setConnectionError(`การเชื่อมต่อขาดหาย (รหัส: ${event.code})`);

          if (reconnectAttemptsRef.current < maxReconnectAttempts) {
            const backoffDelay = calculateBackoffDelay(
              reconnectAttemptsRef.current,
              baseReconnectInterval,
              maxReconnectInterval
            );
            reconnectAttemptsRef.current++;
            console.log(
              `Attempting to reconnect in ${backoffDelay}ms... ` +
              `(attempt ${reconnectAttemptsRef.current}/${maxReconnectAttempts})`
            );

            reconnectTimeoutRef.current = setTimeout(() => {
              if (!isConnectedRef.current && !isConnectingRef.current) {
                connect();
              }
            }, backoffDelay);
          } else {
            setConnectionError('ไม่สามารถเชื่อมต่อได้ กรุณาลองใหม่ภายหลัง');
          }
        }

        onDisconnected?.();
      };

      ws.current.onerror = (error) => {
        console.warn('WebSocket connection error (server may be unavailable). Reconnect will be attempted automatically.');
        setConnectionError('เกิดข้อผิดพลาดในการเชื่อมต่อ');
        isConnectingRef.current = false;
        setIsConnecting(false);
        onError?.(error);
      };

    } catch (error) {
      console.error('Error creating WebSocket connection:', error);
      setConnectionError('ไม่สามารถสร้างการเชื่อมต่อได้');
      isConnectingRef.current = false;
      setIsConnecting(false);
    }
  }, [url, baseReconnectInterval, maxReconnectInterval, maxReconnectAttempts, onConnected, onDisconnected, onError]);

  // ฟังก์ชันสำหรับตัดการเชื่อมต่อ
  const disconnect = useCallback(() => {
    manuallyDisconnectedRef.current = true;

    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    if (tokenChangeReconnectRef.current) {
      clearTimeout(tokenChangeReconnectRef.current);
      tokenChangeReconnectRef.current = null;
    }

    if (ws.current) {
      ws.current.close(1000, 'Manual disconnect');
      ws.current = null;
    }

    isConnectedRef.current = false;
    isConnectingRef.current = false;
    setIsConnected(false);
    setIsConnecting(false);
    setConnectionError(null);
  }, []);

  // ฟังก์ชันสำหรับส่งข้อความ
  const sendMessage = useCallback((message: WebSocketMessage) => {
    if (ws.current?.readyState === WebSocket.OPEN) {
      try {
        ws.current.send(JSON.stringify(message));
        console.log('WebSocket message sent:', message);
      } catch (error) {
        console.error('Error sending WebSocket message:', error);
      }
    } else {
      console.warn('WebSocket is not connected. Cannot send message:', message);
    }
  }, []);

  // Check for token changes via storage events (primary) + fallback polling
  useEffect(() => {
    const checkToken = () => {
      const newFingerprint = getTokenFingerprint();
      setTokenFingerprint(newFingerprint);
    };

    checkToken();
    const interval = setInterval(checkToken, 5000);
    window.addEventListener('storage', checkToken);

    return () => {
      clearInterval(interval);
      window.removeEventListener('storage', checkToken);
    };
  }, []);

  // Auto connect on mount, when token becomes available, or reconnect when token changes
  useEffect(() => {
    const hasToken = !!tokenFingerprint;
    const tokenChanged = prevTokenRef.current !== tokenFingerprint;

    console.log('WebSocket useEffect: checking conditions...', {
      autoConnect,
      hasToken,
      tokenChanged,
      prevToken: prevTokenRef.current ? 'present' : 'none',
      newToken: tokenFingerprint ? 'present' : 'none',
      isConnected: isConnectedRef.current,
      isConnecting: isConnectingRef.current,
      manuallyDisconnected: manuallyDisconnectedRef.current
    });

    const scheduleReconnect = () => {
      if (tokenChangeReconnectRef.current) {
        clearTimeout(tokenChangeReconnectRef.current);
      }
      tokenChangeReconnectRef.current = setTimeout(() => {
        tokenChangeReconnectRef.current = null;
        if (tokenFingerprint && !isConnectedRef.current && !isConnectingRef.current) {
          connect();
        }
      }, 150);
    };

    // Case 1: Token changed (user switched accounts or token refreshed)
    if (tokenChanged && prevTokenRef.current !== null && tokenFingerprint !== null) {
      if (isConnectedRef.current && ws.current?.readyState === WebSocket.OPEN) {
        const newToken = localStorage.getItem('access_token');
        if (newToken) {
          console.log('WebSocket: Token changed, sending token_refresh message...');
          try {
            ws.current.send(JSON.stringify({
              type: 'token_refresh',
              token: newToken
            }));
          } catch (err) {
            console.error('WebSocket: Failed to send token_refresh, will reconnect:', err);
            disconnect();
            scheduleReconnect();
          }
        }
      } else {
        console.log('WebSocket: Token changed! Disconnecting and reconnecting...');
        disconnect();
        scheduleReconnect();
      }
    }
    // Case 2: Token removed (user logged out)
    else if (tokenChanged && tokenFingerprint === null) {
      console.log('WebSocket: Token removed (logout), disconnecting...');
      disconnect();
    }
    // Case 3: New token (user logged in after being logged out)
    else if (tokenChanged && prevTokenRef.current === null && tokenFingerprint !== null) {
      console.log('WebSocket: Fresh login detected, resetting disconnect flag and connecting...');
      manuallyDisconnectedRef.current = false;
      if (autoConnect && !isConnectedRef.current && !isConnectingRef.current) {
        connect();
      }
    }
    // Case 4: Existing token, auto-connect if not already connected
    else if (autoConnect && hasToken && !isConnectedRef.current && !isConnectingRef.current) {
      const autoConnectJustEnabled = !prevAutoConnectRef.current && autoConnect;

      if (manuallyDisconnectedRef.current && autoConnectJustEnabled) {
        console.log('WebSocket: Resetting manuallyDisconnected flag (autoConnect just enabled)');
        manuallyDisconnectedRef.current = false;
      }

      if (!manuallyDisconnectedRef.current) {
        console.log('WebSocket: Token detected, initiating connection...');
        connect();
      } else {
        console.log('WebSocket: Skipping auto-connect (manually disconnected)');
      }
    }
    // Case 5: autoConnect was just disabled (true → false transition) — tear down existing connection
    // S1133 FIX (OBS-S1133-001): honour user toggling "auto-connect" OFF at runtime.
    // Previously this transition was a no-op so the WebSocket stayed connected even after the user
    // explicitly opted out. We disconnect but do NOT set manuallyDisconnectedRef — that flag is reserved
    // for the dedicated Disconnect button so the autoConnect-just-enabled re-connect path still works.
    else if (!autoConnect && prevAutoConnectRef.current && isConnectedRef.current) {
      console.log('WebSocket: autoConnect disabled by user, disconnecting...');
      disconnect();
    }

    prevTokenRef.current = tokenFingerprint;
    prevAutoConnectRef.current = autoConnect;

    return () => {
      // Only disconnect on unmount, not on re-renders
    };
  }, [autoConnect, tokenFingerprint, connect, disconnect]);

  // Separate cleanup effect for unmount
  useEffect(() => {
    return () => {
      disconnect();
    };
  }, [disconnect]);

  // Send heartbeat every 30 seconds
  useEffect(() => {
    if (!isConnected) return;

    const heartbeatInterval = setInterval(() => {
      if (ws.current?.readyState === WebSocket.OPEN) {
        sendMessage({
          type: 'ping',
          payload: { timestamp: new Date().toISOString() }
        });
      }
    }, 30000);

    return () => clearInterval(heartbeatInterval);
  }, [isConnected, sendMessage]);

  return {
    isConnected,
    isConnecting,
    connectionError,
    connect,
    disconnect,
    sendMessage,
  };
};

export default useWebSocket;
